import Button from '../../../components/form-control/Button';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Formik } from 'formik';
import Input from '../../../components/form-control/Input';
import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { IUpdatePartnerQuotaDTO } from '../../../resources/interfaces';
import { updatePartnerFuelQuota } from '../../../api/requests';
import { FaOilCan } from 'react-icons/fa';
import { showToast } from '../../../utils/miscFunctions';

interface IUpdatePartnerQuota {
  onProceed: () => void;
  onClose: () => void;
  partnerId?: string;
}

const UpdatePartnerQuota = ({ onProceed, onClose, partnerId }: IUpdatePartnerQuota) => {
  const [submitting, setSubmitting] = useState<boolean>(false);

  const submitHandler = (values: any) => {
    setSubmitting(true);
    const payload: IUpdatePartnerQuotaDTO = {
      ...values,
      partnerId: partnerId
    };
    if (partnerId) {
      updatePartnerFuelQuota(payload)
        .then((response: any) => {
          showToast.success(response.message ?? 'Partner fuel quota updated successfully');
          onProceed();
        })
        .catch((error: any) => {
          showToast.error(error?.description ?? 'Failed to update partner fuel quota');
        })
        .finally(() => setSubmitting(false));
    }
  };

  return (
    <div className="w-full">
      <div className="bg-primary-main flex justify-between">
        <div className="p-[1rem] flex justify-between w-full items-center">
          <div className="flex items-center justify-center gap-5">
            <div className="flex items-center justify-center bg-white h-[2.5rem] w-[2.5rem] rounded-full gap-3">
              <FaOilCan className="h-5 w-5" />
            </div>
            <h2 className="font-[gilroy-bold] text-lg">Update Partner Quota</h2>
          </div>
          <XMarkIcon onClick={onClose} width={'18px'} height={'18px'} className="cursor-pointer" />
        </div>
      </div>

      <div className="p-5">
        <Formik
          initialValues={{
            amount: '',
            costPerLitres: '',
            partnerId: '',
            quantityInLitres: ''
          }}
          validateOnChange={false}
          validateOnBlur={true}
          validationSchema={Yup.object({
            amount: Yup.number().required('Quota amount is required.'),
            costPerLitres: Yup.number().required('Cost per litre is required.'),
            quantityInLitres: Yup.number().required('Quantity in litres is required.')
          })}
          onSubmit={(values) => {
            submitHandler(values);
          }}>
          {({ values, errors, handleSubmit, handleChange, setFieldValue, handleBlur, touched }) => {
            useEffect(() => {
              if (values.amount && values.costPerLitres) {
                const quantity = (
                  parseFloat(values.amount) / parseFloat(values.costPerLitres)
                ).toFixed(2);
                setFieldValue('quantityInLitres', quantity);
              }
            }, [values.amount, values.costPerLitres, setFieldValue]);

            return (
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="space-y-5">
                  <Input
                    name="amount"
                    label="Amount"
                    value={values.amount}
                    type="number"
                    wrapperClasses=""
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <Input
                    name="costPerLitres"
                    label="Cost per litre"
                    value={values.costPerLitres}
                    type="number"
                    wrapperClasses=""
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <Input
                    name="quantityInLitres"
                    label="Quantity in Litres"
                    value={values.quantityInLitres}
                    type="number"
                    wrapperClasses=""
                    required
                    onChange={handleChange}
                    disabled
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    label="Submit"
                    bgColor="primary"
                    className="w-fit px-6 text-[14px]"
                    loading={submitting}
                    disabled={submitting}
                  />
                </div>
              </form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default UpdatePartnerQuota;
